import React, { useState, useCallback, useEffect, useMemo } from "react";
import {
  fetchToAndFroMovements,
  fetchToAndFroFilterOptions,
  fetchToAndFroStats,
  fetchToAndFroMasters,
  getToAndFroMovementById,
  updateToAndFroMovement,
  deleteToAndFroMovement,
  returnToAndFroMovement,
} from "../api/toAndFroApi";
import ToAndFroDashboardMetricsSection from "../components/control/sections/ToAndFroDashboardMetricsSection";
import ToAndFroDirectorySection, {
  TO_AND_FRO_DIRECTORY_COLUMN_DEFS,
  createInitialToFroColumnVisibility,
  toFroDefaultDirectoryFilters,
  toFroDirectoryCellValue,
} from "../components/control/sections/ToAndFroDirectorySection";
import ToAndFroMastersSection from "../components/control/sections/ToAndFroMastersSection";
import ToAndFroQuickJumpSection from "../components/control/sections/ToAndFroQuickJumpSection";
import ToAndFroMovementModalsSection from "../components/control/sections/ToAndFroMovementModalsSection";
import GlobalFeedback from "../../../global/GlobalFeedback";
import GlobalSuccessFlashModal from "../../../global/GlobalSuccessFlashModal";
import { useToAndFroRole } from "../utils/useToAndFroRole";
import { TOS_FROS_CONTROL_SUBMODULE_SLUG } from "../utils/toAndFroPermissionSlugs";
import { toMysqlDateTime } from "../utils/mysqlDatetime";
import {
  datetimeLocalNow,
  validateReturnInTimeAgainstOut,
} from "../utils/datetimeLocalBounds";

import "../../user-builder/styles/userBuilder.css";
import "../../user-builder/styles/UserBuilderPage.css";

function safeErrorText(err, fallback = "Internal server error. Please try again later.") {
  const m = err?.message;
  return typeof m === "string" && m.trim() ? m : fallback;
}

/** Normalize API row for admin datetime-local / select controls */
function movementToAdminFormState(m) {
  if (!m) return null;
  const dt = (v) => {
    if (v == null || v === "") return "";
    const s = String(v).trim().replace(" ", "T");
    return s.length >= 16 ? s.slice(0, 16) : s;
  };
  let dateStr = "";
  if (m.movement_date) {
    const s = String(m.movement_date);
    dateStr = s.length >= 10 ? s.slice(0, 10) : dt(m.movement_date).slice(0, 10);
  } else if (m.out_time) {
    dateStr = String(m.out_time).replace(" ", "T").slice(0, 10);
  }
  return {
    ...m,
    movement_date: dateStr,
    out_time: dt(m.out_time),
    in_time: m.in_time ? dt(m.in_time) : "",
    purpose_id: m.purpose_id != null ? String(m.purpose_id) : "",
    status_id: m.status_id != null ? String(m.status_id) : "",
    staff_user_id: m.staff_user_id != null ? String(m.staff_user_id) : "",
  };
}

function buildMovementUpdatePayload(m) {
  if (!m) return {};
  const payload = {};
  if (m.movement_code != null && String(m.movement_code).trim() !== "") {
    payload.movement_code = String(m.movement_code).trim();
  }
  if (m.movement_date != null && String(m.movement_date).trim() !== "") {
    payload.movement_date = String(m.movement_date).trim().slice(0, 10);
  }
  if (m.staff_user_id !== "" && m.staff_user_id != null) {
    const n = Number(m.staff_user_id);
    if (Number.isFinite(n)) payload.staff_user_id = n;
  }
  if (m.designation != null) payload.designation = m.designation;
  if (m.place_visited != null) payload.place_visited = m.place_visited;
  if (m.purpose_id !== "" && m.purpose_id != null) {
    const n = Number(m.purpose_id);
    if (Number.isFinite(n)) payload.purpose_id = n;
  }
  if (m.status_id !== "" && m.status_id != null) {
    const n = Number(m.status_id);
    if (Number.isFinite(n)) payload.status_id = n;
  }
  if (m.out_time) {
    const o = toMysqlDateTime(m.out_time);
    if (o) payload.out_time = o;
  }
  if (m.in_time) {
    const i = toMysqlDateTime(m.in_time);
    if (i) payload.in_time = i;
  }
  if (m.remarks != null) payload.remarks = m.remarks;
  return payload;
}

const DIRECTORY_PAGE_SIZE = 10;

const QUICK_JUMP_BASE = [
  { key: "stats", label: "Performance overview", targetId: "tf-stats" },
  { key: "list", label: "Directory", targetId: "tf-list" },
  { key: "masters", label: "Master tables", targetId: "tf-masters" },
];

const ToAndFroPage = () => {
  const perms = useToAndFroRole(TOS_FROS_CONTROL_SUBMODULE_SLUG);
  const canEditActions = !perms.permissionsLoading && perms.canEdit;
  const canDeleteActions = !perms.permissionsLoading && perms.canDelete;
  const canReturnActions = !perms.permissionsLoading && perms.canReturn;
  const [stats, setStats] = useState(null);
  const [movements, setMovements] = useState([]);
  const [masters, setMasters] = useState(null);
  const [loadingStats, setLoadingStats] = useState(true);
  const [loadingMovements, setLoadingMovements] = useState(true);
  const [directoryPage, setDirectoryPage] = useState(1);
  const [directoryTotal, setDirectoryTotal] = useState(0);
  const [directoryTotalPages, setDirectoryTotalPages] = useState(1);
  const [draftDirectoryFilters, setDraftDirectoryFilters] = useState(() =>
    toFroDefaultDirectoryFilters(),
  );
  const [appliedDirectoryFilters, setAppliedDirectoryFilters] = useState(() =>
    toFroDefaultDirectoryFilters(),
  );
  const [columnVisibility, setColumnVisibility] = useState(createInitialToFroColumnVisibility);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [activeQuickJump, setActiveQuickJump] = useState(null);

  const [selectedMovement, setSelectedMovement] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [movementToDelete, setMovementToDelete] = useState(null);
  const [modalBusy, setModalBusy] = useState(false);

  const getToAndFroExportData = useCallback(() => {
    const visibleCols = TO_AND_FRO_DIRECTORY_COLUMN_DEFS.filter((c) => columnVisibility[c.id]);
    const headers = visibleCols.map((c) => c.label);
    const rows = movements.map((m) =>
      visibleCols.map((c) => {
        const v = toFroDirectoryCellValue(c.id, m, masters);
        return typeof v === "string" || typeof v === "number" ? v : String(v ?? "");
      }),
    );
    return { headers, rows };
  }, [movements, columnVisibility, masters]);

  const directoryQueryParams = useMemo(() => {
    const q = {
      page: directoryPage,
      limit: DIRECTORY_PAGE_SIZE,
    };
    const user = String(appliedDirectoryFilters.user_search || "").trim();
    if (user) q.name = user;

    const col = appliedDirectoryFilters.column;
    const val = String(appliedDirectoryFilters.value || "").trim();
    if (col && val) {
      if (col === "movement_code") q.movement_code = val;
      if (col === "movement_date") q.movement_date = val;
      if (col === "staff_name") q.staff_name = val;
      if (col === "designation") q.designation = val;
      if (col === "purpose_name") {
        const p = (masters?.purposes || []).find((x) => String(x.purpose_name) === val);
        if (p?.id != null) q.purpose_id = String(p.id);
      }
      if (col === "place_visited") q.place_visited = val;
      if (col === "status_name") {
        const s = (masters?.statuses || []).find((x) => String(x.status_name) === val);
        if (s?.id != null) q.status_id = String(s.id);
      }
      if (col === "out_time") q.out_time_contains = val;
      if (col === "in_time") q.in_time_contains = val;
      if (col === "recorded_by" && val) q.created_by_user_id = String(val).trim();
    }
    return q;
  }, [directoryPage, appliedDirectoryFilters, masters]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await fetchToAndFroStats();
      if (response.success) {
        setStats(response.data ?? response);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingStats(false);
    }
  }, []);

  const loadMasters = useCallback(async () => {
    try {
      const response = await fetchToAndFroMasters();
      if (response.success) {
        setMasters(response.data ?? response);
      }
    } catch (err) {
      console.error(err);
    }
  }, []);

  const fetchMovementsList = useCallback(async () => {
    setLoadingMovements(true);
    setErrorMsg("");
    try {
      const data = await fetchToAndFroMovements(directoryQueryParams);
      if (!data?.success) {
        setErrorMsg(data?.message || "Failed to load movements");
        setMovements([]);
        return;
      }
      setMovements(data.movements || []);
      const meta = data.meta || {};
      setDirectoryTotal(Number(meta.total || 0));
      setDirectoryTotalPages(Math.max(1, Number(meta.totalPages || 1)));
      setDirectoryPage(Number(meta.page || 1));
    } catch (err) {
      setErrorMsg(safeErrorText(err));
      setMovements([]);
    } finally {
      setLoadingMovements(false);
    }
  }, [directoryQueryParams]);

  useEffect(() => {
    if (perms.permissionsLoading || !perms.canView) return;
    fetchStats();
  }, [fetchStats, perms.permissionsLoading, perms.canView]);

  useEffect(() => {
    if (perms.permissionsLoading || !perms.canView) return;
    loadMasters();
  }, [loadMasters, perms.permissionsLoading, perms.canView]);

  useEffect(() => {
    const t = setTimeout(() => setAppliedDirectoryFilters({ ...draftDirectoryFilters }), 350);
    return () => clearTimeout(t);
  }, [draftDirectoryFilters]);

  useEffect(() => {
    setDirectoryPage(1);
  }, [appliedDirectoryFilters]);

  useEffect(() => {
    if (perms.permissionsLoading || !perms.canView) return;
    fetchMovementsList();
  }, [fetchMovementsList, perms.permissionsLoading, perms.canView]);

  const bumpAfterMutation = useCallback(() => {
    fetchStats();
    fetchMovementsList();
  }, [fetchStats, fetchMovementsList]);

  const loadDirectoryFilterOptions = useCallback(async (column) => {
    const res = await fetchToAndFroFilterOptions(column);
    if (!res?.success) return [];
    return res.data?.options ?? [];
  }, []);

  const handleViewMovement = useCallback(async (movement) => {
    try {
      const response = await getToAndFroMovementById(movement.id);
      if (response.success) {
        setSelectedMovement(response.data);
        setShowViewModal(true);
      } else {
        setErrorMsg(response.message || "Could not load movement");
      }
    } catch (err) {
      setErrorMsg(safeErrorText(err));
    }
  }, []);

  const handleEditMovement = useCallback(async (movement) => {
    try {
      const response = await getToAndFroMovementById(movement.id);
      if (response.success) {
        setSelectedMovement({
          ...movementToAdminFormState(response.data),
          __rawOutTime: response.data?.out_time,
        });
        setShowEditModal(true);
      } else {
        setErrorMsg(response.message || "Could not load movement");
      }
    } catch (err) {
      setErrorMsg(safeErrorText(err));
    }
  }, []);

  const handleReturnMovement = useCallback(async (movement) => {
    try {
      const response = await getToAndFroMovementById(movement.id);
      if (response.success) {
        setSelectedMovement({
          ...movementToAdminFormState(response.data),
          return_remarks: response.data?.return_remarks ?? "",
          __rawOutTime: response.data?.out_time,
        });
        setShowReturnModal(true);
      } else {
        setErrorMsg(response.message || "Could not load movement");
      }
    } catch (err) {
      setErrorMsg(safeErrorText(err));
    }
  }, []);

  const handleDeleteMovement = useCallback((movement) => {
    setMovementToDelete(movement);
    setShowDeleteModal(true);
  }, []);

  const handleMovementChange = useCallback((field, value) => {
    setSelectedMovement((prev) => (prev ? { ...prev, [field]: value } : null));
  }, []);

  const handleViewClose = useCallback(() => {
    setShowViewModal(false);
    setSelectedMovement(null);
  }, []);

  const handleEditClose = useCallback(() => {
    setShowEditModal(false);
    setSelectedMovement(null);
  }, []);

  const handleReturnClose = useCallback(() => {
    setShowReturnModal(false);
    setSelectedMovement(null);
  }, []);

  const handleEditSave = useCallback(async () => {
    if (!selectedMovement) return;
    setModalBusy(true);
    setErrorMsg("");
    try {
      if (selectedMovement.in_time) {
        const order = validateReturnInTimeAgainstOut(
          selectedMovement.in_time,
          selectedMovement.__rawOutTime ?? selectedMovement.out_time,
        );
        if (!order.ok) {
          setErrorMsg(order.message);
          setModalBusy(false);
          return;
        }
        if (selectedMovement.in_time > datetimeLocalNow()) {
          setErrorMsg("In time cannot be in the future");
          setModalBusy(false);
          return;
        }
      }

      const response = await updateToAndFroMovement(
        selectedMovement.id,
        buildMovementUpdatePayload(selectedMovement),
      );
      if (response.success) {
        setSuccessMsg("Movement updated successfully.");
        handleEditClose();
        bumpAfterMutation();
      } else {
        setErrorMsg(response.message || "Update failed");
      }
    } catch (err) {
      setErrorMsg(safeErrorText(err));
    } finally {
      setModalBusy(false);
    }
  }, [selectedMovement, handleEditClose, bumpAfterMutation]);

  const handleReturnSave = useCallback(async () => {
    if (!selectedMovement) return;
    setModalBusy(true);
    setErrorMsg("");
    try {
      const outRef = selectedMovement.__rawOutTime ?? selectedMovement.out_time;
      const order = validateReturnInTimeAgainstOut(selectedMovement.in_time, outRef);
      if (!order.ok) {
        setErrorMsg(order.message);
        setModalBusy(false);
        return;
      }
      if (!selectedMovement.in_time || selectedMovement.in_time > datetimeLocalNow()) {
        setErrorMsg(
          !selectedMovement.in_time ? "In time is required" : "In time cannot be in the future",
        );
        setModalBusy(false);
        return;
      }

      const response = await returnToAndFroMovement(selectedMovement.id, {
        in_time: toMysqlDateTime(selectedMovement.in_time),
        remarks: selectedMovement.return_remarks,
        return_remarks: selectedMovement.return_remarks,
      });
      if (response.success) {
        setSuccessMsg("Movement returned successfully.");
        handleReturnClose();
        bumpAfterMutation();
      } else {
        setErrorMsg(response.message || "Return failed");
      }
    } catch (err) {
      setErrorMsg(safeErrorText(err));
    } finally {
      setModalBusy(false);
    }
  }, [selectedMovement, handleReturnClose, bumpAfterMutation]);

  const handleDeleteModalClose = useCallback(() => {
    setShowDeleteModal(false);
    setMovementToDelete(null);
  }, []);

  const confirmDeleteMovement = useCallback(async () => {
    if (!movementToDelete) return;
    setModalBusy(true);
    setErrorMsg("");
    try {
      const response = await deleteToAndFroMovement(movementToDelete.id);
      if (response.success) {
        setSuccessMsg("Movement deleted successfully.");
        handleDeleteModalClose();
        bumpAfterMutation();
      } else {
        setErrorMsg(response.message || "Delete failed");
      }
    } catch (err) {
      setErrorMsg(safeErrorText(err));
    } finally {
      setModalBusy(false);
    }
  }, [movementToDelete, handleDeleteModalClose, bumpAfterMutation]);

  const onMasterNotify = useCallback((msg, opts) => {
    const m = typeof msg === "string" ? msg : msg?.message;
    if (!m) return;
    if (opts?.variant === "error") {
      setErrorMsg(m);
    } else {
      setSuccessMsg(m);
    }
  }, []);

  const quickJumpItems = useMemo(() => {
    const items = [];
    if (perms.canView) {
      items.push(QUICK_JUMP_BASE[0], QUICK_JUMP_BASE[1]);
    }
    if (perms.canManageMasters) {
      items.push(QUICK_JUMP_BASE[2]);
    }
    return items;
  }, [perms.canView, perms.canManageMasters]);

  return (
    <div className="user-builder-shell">
      <header className="user-builder-hero">
        <div className="user-builder-hero__text">
          <p className="user-builder-hero__eyebrow">Administrative area</p>
          <h2 className="user-builder-hero__title">To and Fro management</h2>
          <p className="user-builder-hero__sub">
            Track staff and volunteer movements, review the directory, and maintain movement
            purposes.
          </p>
        </div>
      </header>

      <GlobalSuccessFlashModal message={successMsg} onDismiss={() => setSuccessMsg("")} />
      <GlobalFeedback type="error" text={errorMsg} />
      <GlobalFeedback type="error" text={perms.permissionsError} role="alert" />
      {perms.permissionsLoading ? (
        <p className="schedule-table-loading" style={{ padding: "2rem 1.5rem" }}>
          Loading permissions…
        </p>
      ) : null}
      {!perms.permissionsLoading && !perms.canView && !perms.canManageMasters ? (
        <GlobalFeedback
          type="warning"
          text="No permission for to-and-fro control. Enable View on tos-fros-control in User builder, then sign in again."
          role="status"
        />
      ) : null}

      <div className="user-builder-layout">
        <div className="user-builder-layout__main">
          {!perms.permissionsLoading && perms.canView ? (
            <ToAndFroDashboardMetricsSection stats={stats} loadingStats={loadingStats} />
          ) : null}

          {!perms.permissionsLoading && perms.canView ? (
            <ToAndFroDirectorySection
              movements={movements}
              masters={masters}
              fetchDirectoryFilterOptions={loadDirectoryFilterOptions}
              draftDirectoryFilters={draftDirectoryFilters}
              onDraftDirectoryFiltersChange={setDraftDirectoryFilters}
              onClearDirectoryFilters={() => setDraftDirectoryFilters(toFroDefaultDirectoryFilters())}
              columnVisibility={columnVisibility}
              onColumnVisibilityChange={setColumnVisibility}
              getToAndFroExportData={perms.canExport ? getToAndFroExportData : undefined}
              loadingMovements={loadingMovements}
              directoryPage={directoryPage}
              directoryTotalPages={directoryTotalPages}
              directoryTotal={directoryTotal}
              directoryPageSize={DIRECTORY_PAGE_SIZE}
              onDirectoryPageChange={setDirectoryPage}
              onView={handleViewMovement}
              onEdit={canEditActions ? handleEditMovement : undefined}
              onReturn={canReturnActions ? handleReturnMovement : undefined}
              onDelete={canDeleteActions ? handleDeleteMovement : undefined}
            />
          ) : null}

          {!perms.permissionsLoading && perms.canManageMasters ? (
            <ToAndFroMastersSection onPurposeMastersChange={loadMasters} onNotify={onMasterNotify} />
          ) : null}

          <ToAndFroMovementModalsSection
            masters={masters}
            selectedMovement={selectedMovement}
            showViewModal={showViewModal}
            showEditModal={showEditModal}
            showReturnModal={showReturnModal}
            showDeleteModal={showDeleteModal}
            movementToDelete={movementToDelete}
            loading={modalBusy}
            onViewClose={handleViewClose}
            onEditClose={handleEditClose}
            onReturnClose={handleReturnClose}
            onDeleteModalClose={handleDeleteModalClose}
            onMovementChange={handleMovementChange}
            onEditSave={handleEditSave}
            onReturnSave={handleReturnSave}
            onConfirmDelete={confirmDeleteMovement}
          />
        </div>

        {quickJumpItems.length > 0 ? (
          <ToAndFroQuickJumpSection
            asideAriaLabel="To and Fro sections"
            items={quickJumpItems}
            activeKey={activeQuickJump}
            onActiveChange={setActiveQuickJump}
          />
        ) : null}
      </div>
    </div>
  );
};

export default ToAndFroPage;
